import { useState, useEffect, useRef, useMemo } from 'react';
import { Store, Package, ShoppingCart, DollarSign, TrendingUp, Activity, Clock, Users, RefreshCw, AlertCircle, ExternalLink, Eye } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { CHART_COLORS } from '../utils/analyticsDataMapper';
import { resolveProductLineCa, formatEuro } from '../utils/productAnalytics';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { buildAnalyticsUrl } from '../utils/analyticsNavigation';
import SystemHealthCards from '../components/SystemHealthCards';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const REFRESH_INTERVAL = 60000; // 60 secondes
const isProduction = process.env.NODE_ENV === 'production';

// Hook pour détecter la visibilité de la page
const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

// Formatage du temps écoulé
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return `il y a ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

// Calcul des seuils de couleur pour KPI
const getKpiStatus = (value, thresholds) => {
  if (!thresholds) return 'neutral';
  if (value >= thresholds.good) return 'good';
  if (value >= thresholds.warning) return 'warning';
  return 'critical';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCommercants: 0,
    totalProduits: 0,
    totalPrestations: 0,
    totalCommandes: 0,
    totalUsers: 0,
    totalRevenus: 0,
    panierMoyen: 0,
    periodLabel: '30 derniers jours',
    commercantsActifs: 0,
    totalVuesGlobales: 0,
    totalVuesCommercants: 0,
    totalVuesTotal: 0,
    commandesRecentes: [],
    commandesParStatut: [],
    commandesParCanal: [],
    commandesParPaiement: [],
    topProduits: [],
    evolutionCommandes: [],
    systemHealth: {
      health: null,
      clientLatency: null,
      checkedAt: null,
    }
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [healthRefreshing, setHealthRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const isVisible = usePageVisibility();

  const refreshSystemHealth = async () => {
    setHealthRefreshing(true);
    try {
      const healthStarted = Date.now();
      const health = await apiService.getHealthStatus();
      setStats((prev) => ({
        ...prev,
        systemHealth: {
          health,
          clientLatency: Date.now() - healthStarted,
          checkedAt: new Date(),
        },
      }));
    } catch {
      setStats((prev) => ({
        ...prev,
        systemHealth: { health: null, clientLatency: null, checkedAt: new Date() },
      }));
    } finally {
      setHealthRefreshing(false);
    }
  };

  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) {
        setRefreshing(true);
      }
      const loadStarted = Date.now();
      const healthStarted = Date.now();
      const [data, healthPayload] = await Promise.all([
        apiService.getDashboardStats({ period: '30j' }),
        apiService.getHealthStatus().then((health) => ({
          health,
          clientLatency: Date.now() - healthStarted,
        })).catch(() => ({ health: null, clientLatency: null })),
      ]);

      setStats({
        totalCommercants: data.totalCommercants || 0,
        totalProduits: data.totalProduits || 0,
        totalPrestations: data.totalPrestations ?? 0,
        totalCommandes: data.totalCommandes || 0,
        totalUsers: data.totalUsers || 0,
        totalRevenus: data.totalRevenus || 0,
        panierMoyen: data.panierMoyen ?? 0,
        periodLabel: data.periodLabel || '30 derniers jours',
        commercantsActifs: data.commercantsActifs ?? data.totalCommercants,
        totalVuesGlobales: data.totalVuesGlobales || 0,
        totalVuesCommercants: data.totalVuesCommercants || 0,
        totalVuesTotal: data.totalVuesTotal || 0,
        commandesRecentes: data.commandesRecentes || [],
        commandesParStatut: data.commandesParStatut || [],
        commandesParCanal: data.commandesParCanal || [],
        commandesParPaiement: data.commandesParPaiement || [],
        topProduits: data.topProduits || [],
        evolutionCommandes: data.evolutionCommandes || [],
        systemHealth: {
          health: healthPayload.health,
          clientLatency: healthPayload.clientLatency,
          checkedAt: new Date(),
        },
      });
      setLastUpdated(new Date(loadStarted));
      setError('');
      setRetryCount(0);
    } catch (err) {
      if (!silent) {
        setError('Erreur lors du chargement des données');
      }
      if (!isProduction) {
        console.error('Dashboard error:', err);
      }
      
      // Retry avec backoff exponentiel
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchDashboardData(silent);
        }, delay);
      } else if (!silent) {
        setStats({
          totalCommercants: 0,
          totalProduits: 0,
          totalPrestations: 0,
          totalCommandes: 0,
          totalUsers: 0,
          totalRevenus: 0,
          totalVuesGlobales: 0,
          totalVuesCommercants: 0,
          commandesRecentes: [],
          commandesParStatut: [],
          commandesParCanal: [],
          commandesParPaiement: [],
          topProduits: [],
          evolutionCommandes: [],
          systemHealth: { health: null, clientLatency: null, checkedAt: null }
        });
      }
    } finally {
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh toutes les 60 secondes, seulement si page visible
    const setupInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      if (isVisible) {
        intervalRef.current = setInterval(() => {
          fetchDashboardData(true);
        }, REFRESH_INTERVAL);
      }
    };

    setupInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isVisible]);

  const totalCatalogue = stats.totalProduits + stats.totalPrestations;
  const panierMoyen = stats.panierMoyen ?? 0;
  const periodHint = stats.periodLabel || '30 derniers jours';
  const vuesBoutiques = stats.totalVuesCommercants || 0;
  const moyenneVues =
    stats.totalCommercants > 0
      ? Math.round(vuesBoutiques / stats.totalCommercants)
      : 0;

  const statsCards = useMemo(() => [
    {
      name: 'CA net ventes',
      value: `${stats.totalRevenus.toFixed(2)} €`,
      change: `Période : ${periodHint} · hors émission Gift · remboursements déduits`,
      changeType: getKpiStatus(stats.totalRevenus, { good: 1000, warning: 500 }),
      icon: DollarSign,
      linkTo: buildAnalyticsUrl({ section: 'revenus', period: '30j' }),
      thresholds: { good: 1000, warning: 500 }
    },
    {
      name: 'Total Commandes',
      value: stats.totalCommandes.toString(),
      change: `${stats.totalCommandes} sur la période`,
      changeType: getKpiStatus(stats.totalCommandes, { good: 50, warning: 20 }),
      icon: ShoppingCart,
      linkTo: buildAnalyticsUrl({ section: 'commandes', period: '30j' }),
      thresholds: { good: 50, warning: 20 }
    },
    {
      name: 'Panier moyen',
      value: `${panierMoyen.toFixed(2)} €`,
      change: panierMoyen >= 30 ? 'Excellent' : panierMoyen >= 15 ? 'Bon' : 'À améliorer',
      changeType: getKpiStatus(panierMoyen, { good: 30, warning: 15 }),
      icon: TrendingUp,
      linkTo: buildAnalyticsUrl({ section: 'revenus', period: '30j' }),
      thresholds: { good: 30, warning: 15 }
    },
    {
      name: 'Vues boutiques',
      value: vuesBoutiques.toLocaleString('fr-FR'),
      change:
        vuesBoutiques > 0
          ? `Moy. ${moyenneVues} / boutique · ${periodHint}`
          : `Aucune vue · ${periodHint}`,
      changeType: vuesBoutiques > 0 ? 'good' : 'neutral',
      icon: Eye,
      linkTo: buildAnalyticsUrl({ section: 'traffic', period: '30j' }),
      thresholds: { good: 100, warning: 50 }
    },
    {
      name: 'Commerçants',
      value: stats.totalCommercants.toString(),
      change:
        stats.commercantsActifs > 0
          ? `${stats.commercantsActifs} actifs sur ${stats.totalCommercants}`
          : 'Aucun',
      changeType: stats.commercantsActifs > 0 ? 'good' : 'neutral',
      icon: Store,
      linkTo: '/commercants',
      thresholds: { good: 10, warning: 5 }
    },
    {
      name: 'Produits & Prestations',
      value: totalCatalogue.toString(),
      change: totalCatalogue > 0
        ? `${stats.totalProduits} produits · ${stats.totalPrestations} prestations`
        : 'Aucun',
      changeType: totalCatalogue > 0 ? 'good' : 'neutral',
      icon: Package,
      linkTo: buildAnalyticsUrl({ section: 'produits', period: '30j' }),
      thresholds: { good: 100, warning: 50 }
    }
  ], [stats, panierMoyen, totalCatalogue, periodHint, vuesBoutiques, moyenneVues]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'warning': return 'text-orange-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'good': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-orange-50 border-orange-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  // Skeleton loading
  if (loading) {
    return (
      <div className="space-y-6" role="main" aria-label="Dashboard">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" role="main" aria-label="Dashboard">
      {/* Header avec last updated et refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            Vue d&apos;ensemble · {periodHint}
            <span className="text-gray-400"> — détail et dates personnalisées dans Analytics</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Dernière mise à jour : {formatTimeAgo(lastUpdated)}</span>
            </div>
          )}
          <button
            onClick={() => fetchDashboardData()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Actualiser les données"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4" role="alert">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
              <div className="mt-2">
                <button
                  onClick={() => fetchDashboardData()}
                  className="text-sm text-red-700 underline hover:text-red-900"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {statsCards.map((stat) => (
          <div
            key={stat.name}
            className={`card border-2 transition hover:shadow-md cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 ${getStatusBg(stat.changeType)}`}
            role="link"
            tabIndex={0}
            aria-label={`Voir le détail de ${stat.name}`}
            onClick={() => {
              if (stat.linkTo) navigate(stat.linkTo);
            }}
            onKeyDown={(e) => {
              if (!stat.linkTo) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(stat.linkTo);
              }
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center flex-1">
                <div className="flex-shrink-0">
                  <stat.icon className={`h-8 w-8 ${getStatusColor(stat.changeType)}`} aria-hidden="true" />
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <dt className="text-sm font-medium text-gray-700 truncate">
                    {stat.name}
                  </dt>
                  <dd className="mt-1">
                    <div className={`text-2xl font-semibold ${getStatusColor(stat.changeType)}`}>
                      {stat.value}
                    </div>
                    <div className={`text-xs mt-1 ${getStatusColor(stat.changeType)}`}>
                      {stat.change}
                    </div>
                  </dd>
                </div>
              </div>
              {stat.linkTo && (
                <Link
                  to={stat.linkTo}
                  className="ml-2 flex-shrink-0 text-gray-400 hover:text-primary-600 transition"
                  aria-label={`Voir le détail de ${stat.name} dans Analytics`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Santé système — vue opérationnelle immédiate */}
      <div className="card border-2 border-blue-200 bg-blue-50/80">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" aria-hidden />
            Santé du système
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshSystemHealth}
              disabled={healthRefreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-300 bg-white rounded-lg text-blue-800 hover:bg-blue-50 disabled:opacity-50"
              aria-label="Rafraîchir la santé du système"
            >
              <RefreshCw className={`h-4 w-4 ${healthRefreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <Link
              to={buildAnalyticsUrl({ section: 'system' })}
              className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-1 underline-offset-2 hover:underline"
            >
              Logs &amp; runtime <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <SystemHealthCards
          health={stats.systemHealth.health}
          clientLatency={stats.systemHealth.clientLatency}
          checkedAt={stats.systemHealth.checkedAt}
          compact
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Évolution des commandes */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Évolution des commandes ({periodHint})</h3>
            <Link
              to={buildAnalyticsUrl({ section: 'commandes-evolution', period: '30j' })}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              Analyse détaillée <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          {stats.evolutionCommandes.length > 0 ? (
            <div className="h-64" role="img" aria-label="Graphique d'évolution des commandes">
              <Line
                data={{
                  labels: stats.evolutionCommandes.map(item => 
                    new Date(item.createdAt).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
                  ),
                  datasets: [
                    {
                      label: 'Commandes',
                      data: stats.evolutionCommandes.map(item => item._count.id),
                      borderColor: 'rgb(59, 130, 246)',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      tension: 0.4,
                      fill: true
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Activity className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Aucune donnée sur la période</p>
              </div>
            </div>
          )}
        </div>

        {/* Répartition canaux / paiement */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Répartition des commandes</h3>
            <Link
              to={buildAnalyticsUrl({ section: 'commandes-repartition', period: '30j' })}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              Analyse détaillée <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          {stats.commandesParCanal.length > 0 || stats.commandesParPaiement.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stats.commandesParCanal.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Livraison / Click & collect</p>
                  <div className="h-40">
                    <Doughnut
                      data={{
                        labels: stats.commandesParCanal.map((c) => c.label),
                        datasets: [{
                          data: stats.commandesParCanal.map((c) => c.commandes),
                          backgroundColor: CHART_COLORS,
                          borderWidth: 2,
                          borderColor: '#fff',
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } },
                      }}
                    />
                  </div>
                </div>
              )}
              {stats.commandesParPaiement.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Paiement</p>
                  <div className="h-40">
                    <Doughnut
                      data={{
                        labels: stats.commandesParPaiement.map((p) => p.label),
                        datasets: [{
                          data: stats.commandesParPaiement.map((p) => p.commandes),
                          backgroundColor: CHART_COLORS.slice(2),
                          borderWidth: 2,
                          borderColor: '#fff',
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : stats.evolutionCommandes.length > 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-500 px-4">
              <div className="text-center text-sm">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Canaux et paiements indisponibles pour cette vue.</p>
                <p className="text-xs mt-1">Rechargez la page ou ouvrez l&apos;analyse détaillée.</p>
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Aucune commande sur {stats.periodLabel || 'la période'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Produits */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Top Produits</h3>
          <Link
            to={buildAnalyticsUrl({ section: 'produits', period: '30j' })}
            className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
          >
            Analyse détaillée <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        {stats.topProduits.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-gray-500 font-medium">Produit</th>
                  <th className="px-3 py-2 text-left text-gray-500 font-medium">Boutique</th>
                  <th className="px-3 py-2 text-right text-gray-500 font-medium">Ventes</th>
                  <th className="px-3 py-2 text-right text-gray-500 font-medium">CA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.topProduits.slice(0, 5).map((item) => (
                  <tr key={item.produitId || item.produit?.id}>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {item.produit?.nom || 'Produit inconnu'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {item.produit?.commercant?.nom || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{item.quantite ?? item._sum?.quantite ?? 0}</td>
                    <td className="px-3 py-2 text-right">{formatEuro(resolveProductLineCa(item))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Package className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Aucun produit vendu</p>
            </div>
          </div>
        )}
      </div>

      {/* Commandes récentes */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Commandes récentes</h3>
          <Link
            to="/commandes"
            className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
          >
            Voir toutes <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        {stats.commandesRecentes.length > 0 ? (
          <div className="flow-root">
            <ul className="-mb-8" role="list">
              {stats.commandesRecentes.map((commande, index) => (
                <li key={commande.id}>
                  <div className="relative pb-8">
                    {index !== stats.commandesRecentes.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center ring-8 ring-white">
                          <ShoppingCart className="h-4 w-4 text-primary-600" aria-hidden="true" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-900">
                            Commande {commande.numero || `#${commande.id.slice(-8)}`} - {commande.total}€
                          </p>
                          <p className="text-xs text-gray-500">
                            Statut: {commande.statut}
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500 flex items-center gap-1">
                          <Clock className="h-4 w-4" aria-hidden="true" />
                          {new Date(commande.dateCommande || commande.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-center py-8">
            <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-2" aria-hidden="true" />
            <p className="text-gray-500">Aucune commande récente</p>
            <Link
              to="/commandes"
              className="mt-2 text-sm text-primary-600 hover:text-primary-800 inline-flex items-center gap-1"
            >
              Voir toutes les commandes <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
